import { computed } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'activities'
const UI_VIEW = 'temporary_regulations_construction_sites'

export function useTable (options) {
  const {
    trans,
    openConstructionSiteDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn
  } = options || {}

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onCreateNewConstructionSite = () => {
    openConstructionSiteDialogFn({
      title: trans('road-maintenance/temporary_regulation.construction_site_creation'),
      editId: null
    })
  }

  const onShowDetails = (event) => {
    openConstructionSiteDialogFn({
      title: trans('road-maintenance/temporary_regulation.construction_site_update'),
      editId: event?.data?.id
    })
  }

  const deleteConstructionSiteHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`construction-sites/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const onConstructionSiteDelete = (event) => {
    const { name } = event?.data || {}
    const nameTitle = name?.length > 20
      ? name.substring(0, 20) + '...'
      : name || ''

    openInfoDialogFn({
      title: trans('road-maintenance/temporary_regulation.construction_site_delete') + (nameTitle ? ' - ' + nameTitle : ''),
      message: trans('road-maintenance/temporary_regulation.construction_site_delete_confirmation'),
      onConfirm: () => deleteConstructionSiteHandler(event)
    })
  }

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: 'construction-sites'
    }
  }))

  const cellsConfig = freezeObject({
    type: {
      custom_display_value: (data) => {
        return data?.type?.description ? trans(data.type.description) : ''
      }
    }
  })

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/temporary_regulation.construction_site_id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation.name'),
      key: 'name',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation.code'),
      key: 'foreign_code',
      always_visible: true
    }
  ]))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onConstructionSiteDelete)
  ]))

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance.construction_sites'),
        bolded: true
      }
    ]
  }))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans('road-maintenance/temporary_regulation.name')
      },
      {
        key: 'foreign_code',
        label: trans('road-maintenance/temporary_regulation.code')
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig: apiConfig.value,
      filters_and_columns_visibility_config: {
        newBtnPosition: 'start',
        newClick: onCreateNewConstructionSite
      },
      rowDblClick: onShowDetails,
      tableConfig: {
        cellsConfig,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
